import type {Dayjs} from 'dayjs';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime.js';
import utc from 'dayjs/plugin/utc.js';
import timezone from 'dayjs/plugin/timezone.js';
import advancedFormat from 'dayjs/plugin/advancedFormat.js';
import customParseFormat from 'dayjs/plugin/customParseFormat.js';
import type {
  DateInputString,
  HourAndMinuteString,
  ISOString,
  Nullable,
} from '#types/general.js';

dayjs.extend(relativeTime);
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(advancedFormat);
dayjs.extend(customParseFormat);

/** Custom date formats to try when parsing timestamps */
const CUSTOM_DATE_FORMATS = ['YYYY-MM-DD HH:mm:ss Z'];

/**
 * Adds an ordinal suffix to a day of the month (1st, 2nd, 3rd, etc.)
 * @param day - The day of the month (1-31)
 * @returns The day with the appropriate ordinal suffix
 */
export function getOrdinalDayOfMonth(day: number): string {
  switch (day) {
    case 1:
    case 21:
    case 31:
      return `${day}st`;
    case 2:
    case 22:
      return `${day}nd`;
    case 3:
    case 23:
      return `${day}rd`;
    default:
      return `${day}th`;
  }
}

/**
 * Generates a new date by adding/subtracting days, months, or years from the current date
 * @param type - The unit type to modify ('day', 'month', or 'year')
 * @param value - The number of units to add (positive) or subtract (negative)
 * @returns A new Date object representing the calculated date
 */
export function getDateFromNow(
  type: 'day' | 'month' | 'year',
  value: number
): Date {
  const now = new Date();

  switch (type) {
    case 'day': {
      return new Date(now.setDate(now.getDate() + value));
    }
    case 'month': {
      return new Date(now.setMonth(now.getMonth() + value));
    }
    case 'year': {
      return new Date(now.setFullYear(now.getFullYear() + value));
    }
    default:
      throw new Error(`Unsupported date type: ${type}`);
  }
}

/**
 * Determines if the first date is after the second date
 * @param a - The first date to compare
 * @param b - The second date to compare
 * @param unit - The time unit precision for the comparison (default: 'seconds')
 * @returns True if date a is after date b, false otherwise
 */
export function isAfter(
  a: string | number | Date | Dayjs | null,
  b: string | number | Date | Dayjs | null,
  unit: dayjs.OpUnitType = 'seconds'
): boolean {
  return dayjs(a).isAfter(dayjs(b), unit);
}

/**
 * Determines if the first date is before the second date
 * @param a - The first date to compare
 * @param b - The second date to compare
 * @param unit - The time unit precision for the comparison (default: 'seconds')
 * @returns True if date a is before date b, false otherwise
 */
export function isBefore(
  a: string | number | Date | Dayjs | null,
  b: string | number | Date | Dayjs | null,
  unit: dayjs.OpUnitType = 'seconds'
): boolean {
  return dayjs(a).isBefore(dayjs(b), unit);
}

/**
 * Formats a date in a specific timezone in ISO format suitable for datetime inputs
 * @param value - The date to format
 * @param timezone - The timezone to format the date in (default: 'UTC')
 * @returns The formatted date string in YYYY-MM-DDTHH:mm format
 */
export function formatDatetimeInTimezone(
  value: string | number | Date | Dayjs,
  timezone = 'UTC'
): string {
  return dayjs(value).tz(timezone).format('YYYY-MM-DDTHH:mm');
}

/**
 * Formats a date as a relative time string (e.g., "2 hours ago", "in 3 days")
 * @param date - The date to format
 * @returns A human-readable relative time string
 */
export function formatRelativeDate(date: Date | string | number): string {
  const relativeDate = dayjs(new Date(date));
  return relativeDate.fromNow();
}

/**
 * Attempts to parse a timestamp string using custom formats
 * @param timestamp - The timestamp string to parse
 * @returns A valid dayjs object if a matching format is found, undefined otherwise
 * @private
 */
function findValidCustomFormatForTimestamp(
  timestamp: string
): Dayjs | undefined {
  return CUSTOM_DATE_FORMATS.map(format => dayjs(timestamp, format)).find(d =>
    d.isValid()
  );
}

/**
 * Formats a dayjs object as a human-readable date string
 * @param date - The dayjs object to format
 * @param isUtc - Whether to display the date in UTC (default: false)
 * @param wordy - Whether to use a more verbose format with month names (default: true)
 * @returns A formatted date string
 */
function formatDateAsString(date: Dayjs, isUtc = false, wordy = true): string {
  const dateFormat = wordy ? 'MMMM Do, YYYY' : 'MM/DD/YYYY';
  const tzFormat = isUtc ? '[UTC]' : 'z';
  return date.format(`${dateFormat}, hh:mm A (${tzFormat})`);
}

/**
 * Formats a timestamp string as a human-readable date
 * @param timestamp - The timestamp string to format
 * @param isUtc - Whether to interpret and display the date in UTC (default: false)
 * @param wordy - Whether to use a more verbose format with month names (default: true)
 * @returns A formatted date string or the original timestamp if parsing fails
 */
export function formatTimestamp(
  timestamp: string,
  isUtc = false,
  wordy = true
): string {
  const dateJs = isUtc ? dayjs.utc(timestamp) : dayjs(timestamp);

  if (dateJs.isValid()) return formatDateAsString(dateJs, isUtc, wordy);

  const fallbackValidDateFormat = findValidCustomFormatForTimestamp(timestamp);

  if (fallbackValidDateFormat) {
    return formatDateAsString(fallbackValidDateFormat, isUtc, wordy);
  }

  return timestamp;
}

/**
 * Converts a date value to a format suitable for HTML date input fields (YYYY-MM-DD)
 *
 * @param value - The date to convert (string, Date object, or Dayjs object)
 * @returns A formatted date string in YYYY-MM-DD format, or an empty string if invalid
 */
export function convertToDateInputValue(
  value: string | Date | Dayjs
): DateInputString | '' {
  const date = dayjs(value);
  if (!date.isValid()) return '';
  return date.format('YYYY-MM-DD') as DateInputString;
}

/**
 * Converts a date value to ISO format (YYYY-MM-DDTHH:mm) suitable for datetime operations
 *
 * @param value - The date to convert (string, Date object, or Dayjs object)
 * @returns A formatted ISO string in YYYY-MM-DDTHH:mm format
 */
export function convertToISOString(value: string | Date | Dayjs): ISOString {
  const date = dayjs(value);
  return date.format('YYYY-MM-DDTHH:mm');
}

/**
 * Alias for convertToISOString, specifically for datetime-local input fields
 */
export const convertToDatetimeLocalInputValue = convertToISOString;

/**
 * Converts a date value to a format suitable for HTML time input fields (HH:mm)
 *
 * @param value - The date to convert (string, Date object, or Dayjs object)
 * @returns A formatted time string in HH:mm format, or an empty string if invalid
 */
export function convertToTimeInputValue(
  value: string | Date | Dayjs
): HourAndMinuteString | '' {
  const date = dayjs(value);
  if (!date.isValid()) return '';
  return date.format('HH:mm') as HourAndMinuteString;
}

/**
 * Returns the index (0-6) of the day of the week for a given date
 * @param date - The date to get the day of the week index for
 * @returns The index of the day of the week for the given date, or null if the date is invalid
 */
export function getDayOfWeekIndex(
  date: Date | string | number
): Nullable<number> {
  const dayjsDate = dayjs(date);
  if (!dayjsDate.isValid()) return null;
  return dayjsDate.day();
}
